import styled from "styled-components";
import { fontSizes, mediaQueries, spacing, typographyMixin, zIndex } from "@magicbi/ui-library";
import { borderRadius } from "../../../design-system/tokens/borders";
import { shadows } from "../../../design-system/tokens/shadows";
import { dialogMotion, scrimMotion } from "../../../design-system/tokens/overlayMotion";

export const Root = styled.div<{ $insetLeft?: string }>`
  position: fixed;
  inset: 0;
  ${({ $insetLeft }) => ($insetLeft ? `left: ${$insetLeft};` : "")}
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[6]};

  ${mediaQueries.down.sm} {
    padding: ${spacing[3]};
  }
`;

export const Scrim = styled.div<{ $closing?: boolean }>`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.colors.surface.overlay};
  ${({ $closing }) => scrimMotion($closing)}
`;

/** 640px wide, radius 18, palette shadow (F20). */
export const Dialog = styled.div<{ $closing?: boolean }>`
  position: relative;
  ${({ $closing }) => dialogMotion($closing)}
  display: flex;
  flex-direction: column;
  width: min(40rem, 100%);
  max-height: 100%;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.surface.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.stroke};
  border-radius: ${borderRadius[18]};
  box-shadow: ${shadows.component.palette};
`;

/** The dialog's name for screen readers; not drawn. */
export const HiddenTitle = styled.h2`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;

  &:focus {
    outline: none;
  }
`;

/** GAP: 60px high has no token (spacing has 56 and 64); 3.75rem is used. Left padding 18px: spacing 4 (16). */
export const SearchRow = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: ${spacing[3]};
  min-width: 0;
  height: 3.75rem;
  padding: 0 ${spacing[3]} 0 ${spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.stroke};
`;

export const SearchIcon = styled.span`
  display: inline-flex;
  flex: none;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

export const Crumb = styled.span`
  flex: none;
  max-width: 50%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 ${spacing[2]};
  ${typographyMixin("caption", 3)}
  color: ${({ theme }) => theme.colors.primary[500]};
  background: ${({ theme }) => theme.colors.primary[100]};
  border-radius: ${borderRadius.sm};
`;

/** GAP: 15px input text has no token; body 3 (14px) is used. */
export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: ${spacing[1.5]} 0;
  ${typographyMixin("body", 3)}
  color: ${({ theme }) => theme.colors.text.primary};
  background: transparent;
  border: 0;
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`;

export const SearchEnd = styled.div`
  display: inline-flex;
  flex: none;
  align-items: center;
`;

export const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  padding: ${spacing[1.5]} ${spacing[2]} ${spacing[2.5]};
`;

/** 40px, padding 0 18px (spacing 4 = 16, GAP), gap 16. GAP: 11px text has no token; caption 4 (12px) is used. */
export const Footer = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: ${spacing[4]};
  height: ${spacing[10]};
  padding: 0 ${spacing[4]};
  ${typographyMixin("caption", 3)}
  font-size: ${fontSizes["11"]};
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.surface.primary};
  border-top: 1px solid ${({ theme }) => theme.colors.border.stroke};
`;

export const Hints = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[4]};
`;

export const HintsEnd = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[4]};
  margin-left: auto;
`;

export const HintItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1.5]};
`;
